// ServiceDetailModal.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';

const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

const ServiceDetailModal = ({ service, projects, onClose }) => {
    if (!service) return null;

    // Filter projects that match the service title with improved matching
    const relatedProjects = projects.filter(project => {
        if (!project.category) return false;
        
        // Exact match
        if (project.category === service.title) return true;
        
        // Partial match for key words
        const serviceWords = service.title.toLowerCase().split(' ');
        const projectCategory = project.category.toLowerCase();
        
        return serviceWords.some(word => 
            word.length > 2 && projectCategory.includes(word)
        );
    });

    // Helper function to get proper image URL
    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/600x400/1f2937/FFFFFF?text=No+Image';
        
        // Handle base64 data URLs (legacy/direct upload)
        if (url.startsWith('data:image')) {
            return url;
        }
        
        // Handle full URLs (if already complete)
        if (url.startsWith('http')) {
            return url;
        }
        
        // Handle relative paths/filenames
        return `${IMAGE_BASE_URL}${url}`;
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: "easeIn" } }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    variants={modalVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative p-8"
                    onClick={(e) => e.stopPropagation()} // Prevent closing modal when clicking inside
                >
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-black dark:hover:text-white">
                        <X size={24} />
                    </button>

                    {/* Service Header */}
                    <div className="mb-8 border-b dark:border-gray-700 pb-6">
                        <div className="flex items-center gap-4 mb-2">
                            <service.icon size={40} className="text-cyan-500 dark:text-cyan-400" />
                            <h2 className="text-4xl font-bold text-black dark:text-white">{service.title}</h2>
                        </div>
                        <p className="text-lg text-gray-600 dark:text-gray-400">{service.description}</p>
                    </div>

                    {/* Packages Section */}
                    <div className="mb-12">
                        <h3 className="text-3xl font-bold mb-6 text-black dark:text-white">Our Packages</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {service.packages.map((pkg, index) => (
                                <div key={index} className="p-6 rounded-lg border dark:border-gray-700 flex flex-col bg-gray-50 dark:bg-gray-800/50 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:border-cyan-500 dark:hover:border-cyan-500 transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                                    <h4 className="text-xl font-bold mb-2 text-black dark:text-white">{pkg.name}</h4>
                                    <p className="text-3xl font-bold mb-4 text-cyan-500 dark:text-cyan-400">{pkg.price}</p>
                                    <ul className="space-y-2 text-gray-600 dark:text-gray-400 mb-6 flex-grow">
                                        {pkg.features.map((feature, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <CheckCircle size={16} className="text-green-500" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {service.title === "Smart Technologies" && pkg.name === "Basic" ? (
                                        <a
                                            href="https://smarthomelk.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-auto block text-center w-full px-6 py-3 bg-cyan-500 text-black rounded-full font-semibold hover:bg-cyan-400 transition-colors duration-300"
                                        >
                                            Buy Now
                                        </a>
                                    ) : (
                                        <a
                                            href="#contact"
                                            onClick={onClose}
                                            className="mt-auto block text-center w-full px-6 py-3 bg-cyan-500 text-black rounded-full font-semibold hover:bg-cyan-400 transition-colors duration-300"
                                        >
                                            Get Started
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Related Work Section */}
                    {relatedProjects.length > 0 && (
                        <div>
                            <h3 className="text-3xl font-bold mb-6 text-black dark:text-white">Related Work</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {relatedProjects.map((project, index) => (
                                    <div key={project.id || index} className="group">
                                        <div className="relative overflow-hidden rounded-lg mb-2 bg-gray-100 dark:bg-gray-800">
                                            <img 
                                                src={getImageSrc(project.image_url || project.image)} 
                                                alt={project.title} 
                                                className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500 ease-in-out"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://placehold.co/600x400/1f2937/FFFFFF?text=Image+Not+Found';
                                                }}
                                            />
                                            {/* Darker Blue/Gray Overlay - Removes on hover */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-slate-900/75 to-cyan-900/85 group-hover:opacity-0 transition-opacity duration-500"></div>
                                        </div>
                                        <h4 className="text-xl font-bold text-black dark:text-white">{project.title}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{project.category}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ServiceDetailModal;